import { Typography } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../../../../../api/auth/useAuth";
import { useKeyStats } from "../../../../../../../../api/services/financial-integration.hooks";
import type { ColumnType } from "antd/es/table";
import Table from "../../../../../../../components/CustomAntdComponents/Table";
import { LockDisplay } from "../../../../../../../components/LockDisplay/LockDisplay";
import styles from "./KeyStatsTable.module.css";

const { Text } = Typography;

// Интерфейс для данных диапазона цен
interface RangeData {
  min: number;
  max: number;
  current: number;
}

// Стили для компонента диапазона

// Компонент для отображения диапазона цен
const RangeDisplay = ({ min, max, current }: RangeData) => {
  const range = max - min;
  const percentage = range > 0 ? ((current - min) / range) * 100 : 50;

  return (
    <div className={styles.rangeContainer}>
      <Text className={styles.rangeValue}>${min?.toFixed(2)}</Text>
      <div className={styles.rangeBar}>
        <div
          className={styles.rangeFill}
          style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
        />
      </div>
      <Text className={styles.rangeValue}>${max?.toFixed(2)}</Text>
    </div>
  );
};

// Стили для текстовых значений

// Компонент для отображения текстовых значений
const TextValue = ({ value }: { value: string }) => {
  return <Text className={styles.textValue}>{value}</Text>;
};

// Интерфейс для строки таблицы
interface KeyStatsRow {
  key: string;
  metric: string;
  value: React.ReactNode;
}

/* Fills summary grid cell; table body scrolls so row height matches SWOT */

// Функция для форматирования больших чисел
const formatNumber = (num: number): string => {
  if (num >= 1e12) {
    return `$${(num / 1e12)?.toFixed(2)} trillion`;
  }
  if (num >= 1e9) {
    return `$${(num / 1e9)?.toFixed(2)} billion`;
  }
  if (num >= 1e6) {
    return `$${(num / 1e6)?.toFixed(2)} million`;
  }
  if (num >= 1e3) {
    return `$${(num / 1e3)?.toFixed(2)} thousand`;
  }
  return `$${num?.toFixed(2)}`;
};

// Функция для форматирования объема
const formatVolume = (volume: number): string => {
  if (volume >= 1e9) {
    return `${(volume / 1e9)?.toFixed(2)} billion shs`;
  }
  if (volume >= 1e6) {
    return `${(volume / 1e6)?.toFixed(2)} million shs`;
  }
  if (volume >= 1e3) {
    return `${(volume / 1e3)?.toFixed(2)} thousand shs`;
  }
  return `${volume?.toFixed(2)} shs`;
};

interface KeyStatsTableProps {
  tabAccessToken?: string;
}

export const KeyStatsTable = ({ tabAccessToken }: KeyStatsTableProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { symbol } = useParams();
  const naLabel = t("pages.topHalal.info.keyStats.na");

  const { data: keyStatsResponse, isLoading } = useKeyStats({
    symbol: symbol || "",
    tabAccessToken,
  });

  const keyStatsData = keyStatsResponse?.data;

  const tableData: KeyStatsRow[] = useMemo(() => {
    if (!keyStatsData) return [];

    return [
        {
          key: "today-range",
          metric: t("pages.topHalal.info.keyStats.labels.todayRange"),
          value: keyStatsData.todayRange ? (
            <RangeDisplay
              min={keyStatsData.todayRange.low}
              max={keyStatsData.todayRange.high}
              current={
                (keyStatsData.todayRange.low + keyStatsData.todayRange.high) / 2
              }
            />
          ) : (
            <TextValue value={naLabel} />
          ),
        },
        {
          key: "50-day-range",
          metric: t("pages.topHalal.info.keyStats.labels.fiftyDayRange"),
          value: keyStatsData.fiftyDaysRange ? (
            <RangeDisplay
              min={keyStatsData.fiftyDaysRange.low}
              max={keyStatsData.fiftyDaysRange.high}
              current={
                (keyStatsData.fiftyDaysRange.low +
                  keyStatsData.fiftyDaysRange.high) /
                2
              }
            />
          ) : (
            <TextValue value={naLabel} />
          ),
        },
        {
          key: "52-week-range",
          metric: t("pages.topHalal.info.keyStats.labels.fiftyTwoWeekRange"),
          value: keyStatsData.fiftyTwoWeekRange ? (
            <RangeDisplay
              min={keyStatsData.fiftyTwoWeekRange.low}
              max={keyStatsData.fiftyTwoWeekRange.high}
              current={
                (keyStatsData.fiftyTwoWeekRange.low +
                  keyStatsData.fiftyTwoWeekRange.high) /
                2
              }
            />
          ) : (
            <TextValue value={naLabel} />
          ),
        },
        {
          key: "volume",
          metric: t("pages.topHalal.info.keyStats.labels.volume"),
          value: <TextValue value={formatVolume(keyStatsData.volume || 0)} />,
        },
        {
          key: "average-volume",
          metric: t("pages.topHalal.info.keyStats.labels.averageVolume"),
          value: (
            <TextValue value={formatVolume(keyStatsData.averageVolume || 0)} />
          ),
        },
        {
          key: "market-cap",
          metric: t("pages.topHalal.info.keyStats.labels.marketCapitalization"),
          value: (
            <TextValue
              value={formatNumber(keyStatsData.marketCapitalization || 0)}
            />
          ),
        },
        {
          key: "pe-ratio",
          metric: t("pages.topHalal.info.keyStats.labels.peRatio"),
          value: (
            <TextValue value={keyStatsData.peRatio?.toFixed(2) || naLabel} />
          ),
        },
        {
          key: "dividend-yield",
          metric: t("pages.topHalal.info.keyStats.labels.dividendYield"),
          value: (
            <TextValue
              value={
                keyStatsData.dividendYield && keyStatsData.dividendYield > 0
                  ? `${(keyStatsData.dividendYield * 100)?.toFixed(2)}%`
                  : naLabel
              }
            />
          ),
        },
        {
          key: "price-target",
          metric: t("pages.topHalal.info.keyStats.labels.priceTarget"),
          value: (
            <TextValue
              value={
                keyStatsData.priceTarget
                  ? `$${keyStatsData.priceTarget?.toFixed(2)}`
                  : naLabel
              }
            />
          ),
        },
        {
          key: "consensus-rating",
          metric: t("pages.topHalal.info.keyStats.labels.consensusRating"),
          value: (
            <TextValue value={keyStatsData.consensusRating || naLabel} />
          ),
        },
      ];
  }, [keyStatsData, naLabel, t]);

  const titleShort = t("pages.topHalal.info.keyStats.titleShort");

  const columns: ColumnType<KeyStatsRow>[] = [
    {
      title: t("pages.topHalal.info.keyStats.metric"),
      dataIndex: "metric",
      key: "metric",
      render: (text: string) => (
        <Text className={styles.metricName}>{text}</Text>
      ),
    },
    {
      title: t("pages.topHalal.info.keyStats.value"),
      dataIndex: "value",
      key: "value",
    },
  ];

  if (!user) {
    return (
      <div className={styles.outer}>
        <div className={styles.tableTitle}>{titleShort}</div>
        <div className={styles.lockBody}>
          <LockDisplay height="100%" />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.outer}>
        <div className={styles.tableTitle}>{titleShort}</div>
        <div className={styles.loading}>{t("pages.common.loading")}</div>
      </div>
    );
  }

  if (!keyStatsData) {
    return (
      <div className={styles.outer}>
        <div className={styles.tableTitle}>{titleShort}</div>
        <div className={styles.empty}>
          {t("pages.topHalal.info.keyStats.noData")}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.outer}>
      <div className={styles.tableArea}>
        <Table
          className={styles.keyStatsTable}
          title={() => <div className={styles.tableTitle}>{titleShort}</div>}
          columns={columns}
          dataSource={tableData}
          pagination={false}
          showHeader={false}
          bordered={false}
        />
      </div>
    </div>
  );
};
